/**
 * The date format a study's case report forms print, and the one place it is
 * defined (Jim, 2026-10-12).
 *
 * Forms used to print "(mm/dd/yyyy)" on every date question whatever the
 * study's country. The format is now a Study Settings choice
 * (`studySetup.crfDateFormat`), and when nobody has chosen one it is
 * DD-MMM-YYYY (05-JAN-2026), which reads the same in every country.
 *
 * PURE: no IO, no imports, so the web app imports this file directly (the same
 * way it imports other shared functions modules) and the Study Settings
 * choice, the renderer, the Word generator, the guide and the prompts cannot
 * drift apart.
 *
 * A sponsor-supplied form (an uploaded CRF, or a rendering the user attached)
 * is canonical: a format printed on it is never rewritten
 * (`resolveQuestionDateFormat`).
 */

export type StudyDateFormat = "DD-MMM-YYYY" | "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD";

export interface StudyDateFormatChoice {
  value: StudyDateFormat;
  /** A worked example for 5 January 2026, shown next to the choice. */
  example: string;
}

export const STUDY_DATE_FORMATS: readonly StudyDateFormatChoice[] = [
  { value: "DD-MMM-YYYY", example: "05-JAN-2026" },
  { value: "DD/MM/YYYY", example: "05/01/2026" },
  { value: "MM/DD/YYYY", example: "01/05/2026" },
  { value: "YYYY-MM-DD", example: "2026-01-05" },
];

export const DEFAULT_STUDY_DATE_FORMAT: StudyDateFormat = "DD-MMM-YYYY";

/** The stored value as one of the four choices, or null (unset, blank, not a choice). */
export function normalizeStudyDateFormat(raw: unknown): StudyDateFormat | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim().toUpperCase();
  return STUDY_DATE_FORMATS.find((f) => f.value === v)?.value ?? null;
}

/** The study's format: the setting when it is a real choice, else the default. */
export function resolveStudyDateFormat(setting: unknown): StudyDateFormat {
  return normalizeStudyDateFormat(setting) ?? DEFAULT_STUDY_DATE_FORMAT;
}

const PART = "(?:DD|MMM|MM|YYYY)";
const SHAPE = new RegExp(`^${PART}(?:[\\s/.\\-]+${PART}){1,2}$`);
const TIME_TAIL = /\s+H{1,2}[:.]M{1,2}.*$/;

/**
 * THE normaliser for a printed date format: upper case, spacing around a
 * separator collapsed ("dd / mm / yyyy" -> "DD/MM/YYYY"), a time tail dropped.
 * Null for anything that is not a date format: a blank-line mask
 * ("__/__/____"), prose, empty. Everything that compares or prints a format
 * goes through this first.
 */
export function normalizePrintedFormat(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const f = raw
    .trim()
    .toUpperCase()
    .replace(TIME_TAIL, "")
    .replace(/\s*([/.\-])\s*/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  return SHAPE.test(f) && f.includes("YYYY") ? f : null;
}

const printedFormat = normalizePrintedFormat;

/**
 * A form the sponsor supplied: an UPLOADED CRF (it has a source packet). A
 * rendering a user attaches to a form this platform generated does not make it
 * the sponsor's: its questions were generated, and any date format stamped on
 * them is the platform's, not the sponsor's.
 */
export function isSponsorSuppliedForm(f: { sourcePacketPath?: unknown } | null | undefined): boolean {
  return Boolean(f?.sourcePacketPath);
}

/**
 * The one date format the uploaded CRFs print, or null when they print none,
 * no upload exists, or they disagree. Each printed format is normalised
 * before it votes. Content may be an object or a JSON string.
 */
export function sponsorPrintedDateFormat(
  forms: Array<{ content?: unknown; sourcePacketPath?: unknown }>,
): string | null {
  const seen = new Set<string>();
  const walk = (node: unknown, depth: number): void => {
    if (!node || typeof node !== "object" || depth > 12) return;
    if (Array.isArray(node)) {
      for (const n of node) walk(n, depth + 1);
      return;
    }
    const rec = node as Record<string, unknown>;
    const fmt = normalizePrintedFormat(rec.date_format);
    if (fmt) seen.add(fmt);
    for (const v of Object.values(rec)) walk(v, depth + 1);
  };
  for (const f of forms ?? []) {
    if (!isSponsorSuppliedForm(f)) continue;
    let c = f.content;
    if (typeof c === "string") {
      try { c = JSON.parse(c); } catch { continue; }
    }
    walk(c, 0);
  }
  return seen.size === 1 ? [...seen][0] : null;
}

/**
 * The format the study's CRFs are described as using, with where it came from:
 * the Study Settings choice; else the one format the sponsor-supplied CRFs
 * print; else the platform default. Never unknown.
 */
export function resolveStudyDateEntryFormat(args: {
  setting?: unknown;
  sponsorPrinted?: string | null;
}): { value: string; source: "user" | "crf" | "default" } {
  const chosen = normalizeStudyDateFormat(args.setting);
  if (chosen) return { value: chosen, source: "user" };
  const printed = printedFormat(args.sponsorPrinted);
  if (printed) return { value: printed, source: "crf" };
  return { value: DEFAULT_STUDY_DATE_FORMAT, source: "default" };
}


/**
 * The same answer as the server's loader, from documents the web already
 * reads: the project document and its CRF artifacts. Only approved or final
 * forms vote, as on the server.
 */
export function studyDateFormatFromDocs(
  projectData: unknown,
  crfArtifacts: Array<{ status?: unknown; content?: unknown; sourcePacketPath?: unknown }>,
): string {
  const setting = (projectData as { studySetup?: { crfDateFormat?: unknown } } | null | undefined)?.studySetup?.crfDateFormat;
  const approved = (crfArtifacts ?? []).filter((a) => a?.status === "approved" || a?.status === "final");
  return resolveStudyDateEntryFormat({ setting, sponsorPrinted: sponsorPrintedDateFormat(approved) }).value;
}

/** "(dd-mmm-yyyy)": the entry hint printed beside a date answer. */
export function dateHint(format: string): string {
  return `(${format.toLowerCase()})`;
}

const BOXES: Record<string, string> = {
  DD: "__ __",
  MM: "__ __",
  MMM: "__ __ __",
  YYYY: "__ __ __ __",
};

/** The blank date boxes in the renderer's style, e.g. "__ __-__ __ __-__ __ __ __". */
export function dateMask(format: string): string {
  return format.replace(/YYYY|MMM|MM|DD/g, (p) => BOXES[p]);
}

/** A narrow table cell's blank date, e.g. "__-___-____" or "__/__/____". */
export function dateCompactMask(format: string): string {
  return format.replace(/YYYY|MMM|MM|DD/g, (p) => "_".repeat(p.length === 2 ? 2 : p === "MMM" ? 3 : 4));
}

/** The "date not fully known" example: every day and month part is UNK. */
export function partialDateExample(format: string): string {
  return format.replace(/DD|MMM|MM/g, "UNK");
}

/**
 * The format one date question prints.
 *   - a sponsor-supplied form keeps the format printed on it;
 *   - every other form prints the study's format, and the stamp an earlier
 *     platform version left on the question ("mm/dd/yyyy") is ignored, which
 *     is what lets a re-render pick up the Study Settings choice.
 */
export function resolveQuestionDateFormat(args: {
  own?: unknown;
  study?: unknown;
  sponsorSupplied?: boolean;
}): string {
  if (args.sponsorSupplied) {
    const own = printedFormat(args.own);
    if (own) return own;
  }
  // The study's format: a Study Settings choice, or the one format the
  // uploaded CRFs print (which may be outside the four choices).
  return normalizeStudyDateFormat(args.study) ?? printedFormat(args.study) ?? DEFAULT_STUDY_DATE_FORMAT;
}
